import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  Tab,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  SyncAlt as TransferIcon,
  Warehouse as WarehouseIcon,
  LocalShipping as DepartmentIcon,
  Undo as UndoIcon,
  Inventory as InventoryIcon,
  KeyboardReturn as ReturnIcon,
  LocalDrink as ChemicalCanIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import dyeStockService, {
  DyeInkType,
  DyeStockItemHistory,
} from "../services/dyeStock.service";

const DEFAULT_INK_TYPES: { value: DyeInkType; label: string }[] = [
  { value: "digital_reactive", label: "Digital Reactive" },
  { value: "digital_acid", label: "Digital Acid" },
  { value: "digital_sublimation", label: "Digital Sublimation" },
];

const TRANSFER_TYPES: Record<string, string> = {
  warehouse_receipt: "Warehouse Receipt",
  warehouse_to_department: "Warehouse to Printing Department",
  department_issue: "Issue to Machine",
  department_return: "Return to Printing Department",
  adjustment: "Stock Adjustment",
};

const hexToCmyk = (hex: string) => {
  const cleanHex = /^#[0-9a-f]{6}$/i.test(hex) ? hex.slice(1) : "00897B";
  const red = parseInt(cleanHex.slice(0, 2), 16) / 255;
  const green = parseInt(cleanHex.slice(2, 4), 16) / 255;
  const blue = parseInt(cleanHex.slice(4, 6), 16) / 255;
  const black = 1 - Math.max(red, green, blue);
  if (black >= 1) return { c: 0, m: 0, y: 0, k: 100 };
  return {
    c: Math.round(((1 - red - black) / (1 - black)) * 100),
    m: Math.round(((1 - green - black) / (1 - black)) * 100),
    y: Math.round(((1 - blue - black) / (1 - black)) * 100),
    k: Math.round(black * 100),
  };
};

const cmykToHex = ({ c, m, y, k }: { c: number; m: number; y: number; k: number }) => {
  const channel = (value: number) => Math.round(255 * (1 - value / 100) * (1 - k / 100));
  return `#${[channel(c), channel(m), channel(y)].map((value) => value.toString(16).padStart(2, "0")).join("")}`;
};

const emptyTransaction = {
  transaction_type: "warehouse_receipt",
  item_id: "",
  barrels: "",
  kg: "",
  machine_name: "",
  reference_number: "",
  remarks: "",
  transaction_date: "",
  adjustment_location: "warehouse",
};

const DyeStockManagement: React.FC = () => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [inkTypes, setInkTypes] = useState(DEFAULT_INK_TYPES);
  const [categoryFilter, setCategoryFilter] = useState<
    "all" | "ink" | "chemical"
  >("all");
  const [typeFilter, setTypeFilter] = useState<"all" | string>("all");
  const [stockItems, setStockItems] = useState<any[]>([]);
  const [catalog, setCatalog] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(0);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [transactionOpen, setTransactionOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState<any>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [newCatalogItemOpen, setNewCatalogItemOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [transaction, setTransaction] = useState<any>(emptyTransaction);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [newInkTypeName, setNewInkTypeName] = useState("");
  const [catalogForm, setCatalogForm] = useState({
    ink_type: "digital_reactive" as DyeInkType,
    category: "ink" as "ink" | "chemical",
    name: "",
    code: "",
    card_color: "#00897B",
    barrel_size_kg: "",
    monthly_average_usage_kg: "",
  });

  const loadData = async () => {
    try {
      const [items, transactions, allCatalog] = await Promise.all([
        dyeStockService.getDashboard({ search: search.trim() || undefined }),
        dyeStockService.getTransactions(),
        dyeStockService.getItems(),
      ]);
      setStockItems(items);
      setTransactions(transactions);
      setCatalog(allCatalog);
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.response?.data?.error || "Failed to load dye stock data",
      });
    }
  };

  useEffect(() => {
    Promise.all([
      dyeStockService.getTypes().catch(() => []),
      dyeStockService.seedCatalog().catch(() => undefined),
    ]).then(([types]) => {
      const dynamicTypes = (types || []).map((type: any) => ({
        value: type.code,
        label: type.name,
      }));
      setInkTypes(dynamicTypes.length ? dynamicTypes : DEFAULT_INK_TYPES);
      loadData();
    });
  }, []);
  useEffect(() => {
    loadData();
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCatalogItem = catalog.find(
    (item) => Number(item.id) === Number(transaction.item_id),
  );
  const calculatedKg =
    selectedCatalogItem?.barrel_size_kg && transaction.barrels
      ? Number(transaction.barrels) * Number(selectedCatalogItem.barrel_size_kg)
      : 0;
  const selectedStockItem = stockItems.find(
    (item) => Number(item.id) === Number(transaction.item_id),
  );
  const selectedStockLocation = transaction.transaction_type === "department_issue"
    ? "printing_department"
    : transaction.transaction_type === "adjustment"
      ? transaction.adjustment_location
      : transaction.transaction_type === "warehouse_receipt" || transaction.transaction_type === "warehouse_to_department"
        ? "warehouse"
        : "printing_department";
  const selectedStockBalance = selectedStockLocation === "warehouse"
    ? { barrels: selectedStockItem?.warehouse_barrels, kg: selectedStockItem?.warehouse_kg }
    : { barrels: selectedStockItem?.department_barrels, kg: selectedStockItem?.department_kg };

  const filteredItems = stockItems.filter((item) => {
    const matchesSearch =
      !search.trim() ||
      `${item.name} ${item.code || ""} ${item.ink_type || ""}`
        .toLowerCase()
        .includes(search.toLowerCase());
    const matchesCategory =
      categoryFilter === "all" || item.category === categoryFilter;
    const matchesType = typeFilter === "all" || item.ink_type === typeFilter;
    return matchesSearch && matchesCategory && matchesType;
  });

  const openTransaction = (type?: string) => {
    setTransaction({
      ...emptyTransaction,
      transaction_type: type || "warehouse_receipt",
    });
    setTransactionOpen(true);
  };

  const saveTransaction = async () => {
    if (!transaction.item_id) {
      setMessage({ type: "error", text: "Select a dye or chemical" });
      return;
    }
    if (!selectedCatalogItem?.barrel_size_kg) {
      setMessage({
        type: "error",
        text: "Set this item's barrel size in the catalog first",
      });
      return;
    }
    if (
      transaction.transaction_type === "adjustment" &&
      !transaction.adjustment_location
    ) {
      setMessage({
        type: "error",
        text: "Select which stock location to adjust",
      });
      return;
    }
    try {
      await dyeStockService.createTransaction({
        ...transaction,
        item_id: Number(transaction.item_id),
        barrels: Number(transaction.barrels),
        kg: calculatedKg,
        from_location:
          transaction.transaction_type === "warehouse_to_department"
            ? "warehouse"
            : transaction.transaction_type === "department_issue"
              ? "printing_department"
              : transaction.adjustment_location,
        to_location:
          transaction.transaction_type === "warehouse_to_department"
            ? "printing_department"
            : transaction.transaction_type === "department_issue"
              ? "machine"
              : null,
        transaction_date: transaction.transaction_date || undefined,
      });
      setTransactionOpen(false);
      setMessage({ type: "success", text: "Dye stock transaction recorded" });
      await loadData();
    } catch (error: any) {
      setMessage({
        type: "error",
        text:
          error.response?.data?.error || error.message || "Transaction failed",
      });
    }
  };

  const reverseTransaction = async (id: number) => {
    try {
      await dyeStockService.reverseTransaction(id);
      setMessage({
        type: "success",
        text: "Transaction reversed and balances restored",
      });
      await loadData();
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.response?.data?.error || "Could not reverse transaction",
      });
    }
  };

  const openCatalogEdit = (item?: any) => {
    setEditingItem(item || null);
    setNewCatalogItemOpen(!item);
    setCatalogForm(
      item
        ? {
            ink_type: item.ink_type,
            category: item.category,
            name: item.name,
            code: item.code || "",
            card_color: item.card_color || "#00897B",
            barrel_size_kg: item.barrel_size_kg || "",
            monthly_average_usage_kg: item.monthly_average_usage_kg || "",
          }
        : {
            ink_type: typeFilter !== "all" ? typeFilter : "digital_reactive",
            category: "ink",
            name: "",
            code: "",
            card_color: "#00897B",
            barrel_size_kg: "",
            monthly_average_usage_kg: "",
          },
    );
  };
  const saveCatalogItem = async () => {
    if (!catalogForm.name.trim()) {
      setMessage({ type: "error", text: "Name is required" });
      return;
    }
    if (
      !catalogForm.barrel_size_kg ||
      Number(catalogForm.barrel_size_kg) <= 0
    ) {
      setMessage({
        type: "error",
        text: "Enter the weight of one barrel in kg",
      });
      return;
    }
    try {
      editingItem
        ? await dyeStockService.updateItem(editingItem.id, {
            ...catalogForm,
            is_active: true,
          })
        : await dyeStockService.createItem(catalogForm);
      setEditingItem(null);
      setCatalogForm({
        ink_type: typeFilter !== "all" ? typeFilter : "digital_reactive",
        category: "ink",
        name: "",
        code: "",
        card_color: "#00897B",
        barrel_size_kg: "",
        monthly_average_usage_kg: "",
      });
      setMessage({
        type: "success",
        text: editingItem ? "Item updated" : "Item added",
      });
      setNewCatalogItemOpen(false);
      await loadData();
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.response?.data?.error || "Could not save catalog item",
      });
    }
  };
  const deleteCatalogItem = async () => {
    if (!itemToDelete) return;
    try {
      await dyeStockService.deleteItem(itemToDelete);
      setDeleteOpen(false);
      setItemToDelete(null);
      setMessage({ type: "success", text: "Item removed from the active catalog" });
      await loadData();
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.response?.data?.error || "Could not delete catalog item",
      });
    }
  };
  const refreshInkTypes = async () => {
    try {
      const types = await dyeStockService.getTypes();
      const dynamicTypes = (types || []).map((type: any) => ({
        value: type.code,
        label: type.name,
      }));
      setInkTypes(dynamicTypes.length ? dynamicTypes : DEFAULT_INK_TYPES);
    } catch (_) {
      /* Keep the three default tabs available */
    }
  };
  const addInkType = async () => {
    if (!newInkTypeName.trim()) {
      setMessage({ type: "error", text: "Ink type name is required" });
      return;
    }
    try {
      await dyeStockService.createType({ name: newInkTypeName.trim() });
      setNewInkTypeName("");
      await refreshInkTypes();
      setMessage({ type: "success", text: "Ink type added" });
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.response?.data?.error || "Could not add ink type",
      });
    }
  };

  const inkTypeLabel = (code: string) =>
    inkTypes.find((type) => type.value === code)?.label || code;

  const renderCatalogEditor = () => (
    <Grid container spacing={1.5} sx={{ pt: 1 }}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel>Ink Type</InputLabel>
          <Select label="Ink Type" value={catalogForm.ink_type} onChange={(e) => setCatalogForm({ ...catalogForm, ink_type: e.target.value as DyeInkType })}>
            {inkTypes.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel>Category</InputLabel>
          <Select label="Category" value={catalogForm.category} onChange={(e) => setCatalogForm({ ...catalogForm, category: e.target.value as any })}>
            <MenuItem value="ink">Ink</MenuItem><MenuItem value="chemical">Chemical</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={7}><TextField fullWidth size="small" label="Name / Color" value={catalogForm.name} onChange={(e) => setCatalogForm({ ...catalogForm, name: e.target.value })} /></Grid>
      <Grid item xs={12} sm={5}><TextField fullWidth size="small" label="Code" value={catalogForm.code} onChange={(e) => setCatalogForm({ ...catalogForm, code: e.target.value })} /></Grid>
      <Grid item xs={12} sm={6}><TextField fullWidth size="small" required type="number" label="Barrel Size (kg per barrel)" inputProps={{ min: 0.001, step: 0.001 }} value={catalogForm.barrel_size_kg} onChange={(e) => setCatalogForm({ ...catalogForm, barrel_size_kg: e.target.value })} /></Grid>
      <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Monthly Average Usage (kg)" inputProps={{ min: 0, step: 0.001 }} value={catalogForm.monthly_average_usage_kg} onChange={(e) => setCatalogForm({ ...catalogForm, monthly_average_usage_kg: e.target.value })} /></Grid>
      <Grid item xs={12}>
        {(() => {
          const cmyk = hexToCmyk(catalogForm.card_color);
          const updateCmyk = (channel: "c" | "m" | "y" | "k", value: string) => {
            const parsed = Number(value);
            const next = { ...cmyk, [channel]: Number.isFinite(parsed) ? Math.min(100, Math.max(0, parsed)) : 0 };
            setCatalogForm({ ...catalogForm, card_color: cmykToHex(next) });
          };
          return (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Card color (CMYK)</Typography>
              <Grid container spacing={1} alignItems="center">
                {(["c", "m", "y", "k"] as const).map((channel) => (
                  <Grid item xs={3} sm={2} key={channel}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={channel.toUpperCase()}
                      value={cmyk[channel]}
                      onChange={(event) => updateCmyk(channel, event.target.value)}
                      inputProps={{ min: 0, max: 100, step: 1, 'aria-label': `${channel.toUpperCase()} percentage` }}
                    />
                  </Grid>
                ))}
                <Grid item xs={12} sm={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box aria-label="CMYK color preview" sx={{ width: 44, height: 36, bgcolor: catalogForm.card_color, border: "1px solid #9e9e9e", borderRadius: 1 }} />
                    <Typography variant="caption" color="text.secondary">{catalogForm.card_color.toUpperCase()}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          );
        })()}
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button size="small" onClick={() => { setEditingItem(null); setNewCatalogItemOpen(false); }}>Cancel</Button>
          <Button size="small" variant="contained" onClick={saveCatalogItem}>{editingItem ? 'Save Changes' : 'Add to Catalog'}</Button>
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Dye Stock Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Digital dye and chemical inventory in barrels and kilograms
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => {
              setCatalogOpen(true);
              setEditingItem(null);
              setNewCatalogItemOpen(false);
            }}
          >
            Catalog
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openTransaction()}
          >
            Record Stock
          </Button>
        </Box>
      </Box>
      {message && (
        <Alert
          severity={message.type}
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}
      {!mobile && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          <Button
            variant="contained"
            color="success"
            startIcon={<WarehouseIcon />}
            onClick={() => openTransaction("warehouse_receipt")}
          >
            Warehouse Receipt
          </Button>
          <Button
            variant="contained"
            startIcon={<TransferIcon />}
            onClick={() => openTransaction("warehouse_to_department")}
          >
            Transfer to Printing
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<DepartmentIcon />}
            onClick={() => openTransaction("department_issue")}
          >
            Issue to Machine
          </Button>
          <Button
            variant="outlined"
            startIcon={<ReturnIcon />}
            onClick={() => openTransaction("department_return")}
          >
            Return to Department
          </Button>
        </Box>
      )}
      <Grid container spacing={1} mb={2}>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            size="small"
            label="Search all dyes / chemicals"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Ink Type</InputLabel>
            <Select
              label="Ink Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All Ink Types</MenuItem>
              {inkTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as any)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="ink">Ink</MenuItem>
              <MenuItem value="chemical">Chemical</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 1 }}>
        <Tab label="Stock Balance" />
        <Tab label="Transaction Ledger" />
      </Tabs>
      {tab === 0 ? (
        <StockCardGrid
          items={filteredItems}
          inkTypeLabel={inkTypeLabel}
          onItemClick={setHistoryItem}
        />
      ) : mobile ? (
        <MobileLedger
          transactions={transactions}
          reverseTransaction={reverseTransaction}
        />
      ) : (
        <TransactionLedger
          transactions={transactions}
          reverseTransaction={reverseTransaction}
        />
      )}
      {mobile && (
        <SpeedDial
          ariaLabel="Dye stock actions"
          sx={{ position: "fixed", bottom: 24, right: 20 }}
          icon={<SpeedDialIcon />}
        >
          <SpeedDialAction
            icon={<WarehouseIcon />}
            tooltipTitle="Warehouse Receipt"
            onClick={() => openTransaction("warehouse_receipt")}
          />
          <SpeedDialAction
            icon={<TransferIcon />}
            tooltipTitle="Transfer to Printing"
            onClick={() => openTransaction("warehouse_to_department")}
          />
          <SpeedDialAction
            icon={<DepartmentIcon />}
            tooltipTitle="Issue to Machine"
            onClick={() => openTransaction("department_issue")}
          />
          <SpeedDialAction
            icon={<ReturnIcon />}
            tooltipTitle="Return to Department"
            onClick={() => openTransaction("department_return")}
          />
        </SpeedDial>
      )}

      <Dialog
        open={transactionOpen}
        onClose={() => setTransactionOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Record Dye Stock</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Transaction</InputLabel>
                <Select
                  label="Transaction"
                  value={transaction.transaction_type}
                  onChange={(e) =>
                    setTransaction({
                      ...transaction,
                      transaction_type: e.target.value,
                    })
                  }
                >
                  {Object.entries(TRANSFER_TYPES).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Dye / Chemical</InputLabel>
                <Select
                  label="Dye / Chemical"
                  value={transaction.item_id}
                  onChange={(e) =>
                    setTransaction({ ...transaction, item_id: e.target.value })
                  }
                >
                  {catalog.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                      {item.code ? ` - ${item.code}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {selectedCatalogItem && selectedStockItem && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<InventoryIcon />}>
                  Available in {selectedStockLocation === "warehouse" ? "Warehouse Stock" : "Printing Department Stock"}: <strong>{Number(selectedStockBalance.barrels || 0).toFixed(3)} barrels</strong> · <strong>{Number(selectedStockBalance.kg || 0).toFixed(3)} kg</strong>
                </Alert>
              </Grid>
            )}
            {transaction.transaction_type === "adjustment" && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Stock to Adjust</InputLabel>
                  <Select
                    label="Stock to Adjust"
                    value={transaction.adjustment_location}
                    onChange={(e) =>
                      setTransaction({
                        ...transaction,
                        adjustment_location: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="warehouse">Warehouse Stock</MenuItem>
                    <MenuItem value="printing_department">
                      Printing Department Stock
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Barrels"
                value={transaction.barrels}
                onChange={(e) =>
                  setTransaction({ ...transaction, barrels: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Calculated Weight (kg)"
                value={calculatedKg ? calculatedKg.toFixed(3) : ""}
                InputProps={{ readOnly: true }}
                helperText={
                  selectedCatalogItem?.barrel_size_kg
                    ? `${selectedCatalogItem.barrel_size_kg} kg per barrel`
                    : "Set barrel size in the catalog first"
                }
              />
            </Grid>
            {transaction.transaction_type === "department_issue" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Machine Name"
                  value={transaction.machine_name}
                  onChange={(e) =>
                    setTransaction({
                      ...transaction,
                      machine_name: e.target.value,
                    })
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Number"
                value={transaction.reference_number}
                onChange={(e) =>
                  setTransaction({
                    ...transaction,
                    reference_number: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Remarks"
                value={transaction.remarks}
                onChange={(e) =>
                  setTransaction({ ...transaction, remarks: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransactionOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveTransaction}>
            Save Transaction
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
          Dye / Chemical Catalog
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openCatalogEdit()}>
            Add Dye / Chemical
          </Button>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" gap={1} mb={2}>
            <TextField
              fullWidth
              size="small"
              label="New Ink Type"
              value={newInkTypeName}
              onChange={(e) => setNewInkTypeName(e.target.value)}
            />
            <Button variant="outlined" onClick={addInkType}>
              Add Type
            </Button>
          </Box>
          {newCatalogItemOpen && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderLeft: "4px solid #00897B" }}>
              <Typography variant="subtitle2" fontWeight={700}>New catalog item</Typography>
              {renderCatalogEditor()}
            </Paper>
          )}
          <Grid container spacing={1.5}>
            {catalog.map((item) => (
              <Grid item xs={12} sm={6} key={item.id}>
                <Paper variant="outlined" sx={{ p: 1.5, height: "100%", borderTop: `4px solid ${item.card_color || "#00897B"}`, opacity: item.is_active ? 1 : 0.58 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">{inkTypeLabel(item.ink_type)} · {item.category}</Typography>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.code || "No code"} · {item.barrel_size_kg || "Size not set"} kg/barrel · {item.monthly_average_usage_kg || 0} kg/month · {item.is_active ? "Active" : "Inactive"}
                      </Typography>
                    </Box>
                    <Box display="flex" flexShrink={0}>
                      <Tooltip title="Edit item in this card"><IconButton size="small" onClick={() => openCatalogEdit(item)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Remove from active catalog"><IconButton size="small" color="error" onClick={() => { setItemToDelete(item.id); setDeleteOpen(true); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </Box>
                  {editingItem?.id === item.id && <><Divider sx={{ my: 1 }} />{renderCatalogEditor()}</>}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatalogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Catalog Item?</DialogTitle>
        <DialogContent>
          This item will be archived from active stock views. Its history and balances are retained, and default seeding will not restore it.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteCatalogItem}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <DyeStockHistoryDialog
        item={historyItem}
        onClose={() => setHistoryItem(null)}
      />
    </Box>
  );
};

function StockCardGrid({
  items,
  inkTypeLabel,
  onItemClick,
}: {
  items: any[];
  inkTypeLabel: (code: string) => string;
  onItemClick: (item: any) => void;
}) {
  const getItemColor = (item: any) => {
    if (/^#[0-9a-fA-F]{6}$/.test(item.card_color || "")) return item.card_color;
    const text = `${item.name || ""} ${item.code || ""}`.toLowerCase();
    if (text.includes("light black")) return "#9e9e9e";
    if (text.includes("light magenta")) return "#f48fb1";
    if (text.includes("light cyan")) return "#80deea";
    if (text.includes("cyan") || text.includes("blue")) return "#1976d2";
    if (text.includes("magenta")) return "#d81b60";
    if (text.includes("yellow")) return "#f9a825";
    if (text.includes("orange")) return "#ef6c00";
    if (text.includes("black")) return "#263238";
    if (text.includes("cleaning")) return "#7cb342";
    return item.category === "chemical" ? "#455a64" : "#00897b";
  };

  if (!items.length) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">
          No dye or chemical stock matches the current filters.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(1, minmax(0, 1fr))",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(3, minmax(0, 1fr))",
          xl: "repeat(4, minmax(0, 1fr))",
        },
        gap: { xs: 1, sm: 2 },
      }}
    >
      {items.map((item) => {
        const itemColor = getItemColor(item);
        const monthsRemaining = Number(item.months_remaining);
        const hasUsageTarget = Number(item.monthly_average_usage_kg) > 0;
        const warningColor = hasUsageTarget
          ? monthsRemaining < 4
            ? "rgba(244, 67, 54, 0.18)"
            : monthsRemaining <= 6
              ? "rgba(255, 152, 0, 0.18)"
              : null
          : null;
        const monthTextColor = warningColor
          ? monthsRemaining < 4 ? "#c62828" : "#e65100"
          : "#2e7d32";
        return (
          <Paper
            key={item.id}
            variant="outlined"
            role="button"
            tabIndex={0}
            aria-label={`View stock history and analytics for ${item.name}`}
            onClick={() => onItemClick(item)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onItemClick(item);
              }
            }}
            sx={{
              overflow: "hidden",
              borderRadius: 1,
              borderTop: `5px solid ${itemColor}`,
              bgcolor: "background.paper",
              "--stock-glow-color": warningColor || "transparent",
              animation: warningColor ? "stock-card-pulse 2s ease-in-out infinite" : "none",
              "@keyframes stock-card-pulse": {
                "0%, 100%": { backgroundColor: "#ffffff" },
                "50%": { backgroundColor: "var(--stock-glow-color)" },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              cursor: "pointer",
              "&:hover": { boxShadow: 4 },
              "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minHeight: 112,
                px: 2,
                py: 1.5,
                bgcolor: warningColor ? "transparent" : "#fafafa",
              }}
            >
              <Box
                sx={{
                  width: 88,
                  height: 88,
                  minWidth: 88,
                  mr: 2,
                  borderRadius: "14px",
                  bgcolor: itemColor,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "inset 0 -10px 18px rgba(0,0,0,.18)",
                }}
              >
                <ChemicalCanIcon sx={{ fontSize: 58 }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.2 }}
                >
                  {item.name}
                </Typography>
                {item.code && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 700 }}
                  >
                    {item.code}
                  </Typography>
                )}
                <Box
                  sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}
                >
                  <Chip size="small" label={inkTypeLabel(item.ink_type)} />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={item.category === "chemical" ? "Chemical" : "Ink"}
                  />
                </Box>
              </Box>
              <Box sx={{ flexShrink: 0, textAlign: "right", alignSelf: "flex-start", ml: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", fontWeight: 700, lineHeight: 1.2 }}
                >
                  TOTAL STOCK
                </Typography>
                <Typography
                  sx={{
                    color: "text.primary",
                    fontSize: { xs: "2rem", md: "2.25rem" },
                    fontWeight: 800,
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                  }}
                >
                  {Number(item.total_stock_kg || 0).toFixed(2)} kg
                </Typography>
                <Typography
                  sx={{
                    color: hasUsageTarget ? monthTextColor : "text.secondary",
                    fontSize: "1.1rem",
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mt: 0.25,
                  }}
                >
                  {hasUsageTarget ? `${monthsRemaining.toFixed(1)} months` : "Usage target not set"}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderRight: "1px solid",
                  borderColor: "divider",
                  bgcolor: warningColor ? "transparent" : "#f5f9ff",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Warehouse
                </Typography>
                <Typography sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" }, fontWeight: 800, lineHeight: 1 }}>
                  {Number(item.warehouse_kg || 0).toFixed(2)} kg
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {Number(item.warehouse_barrels || 0).toFixed(2)} barrels
                </Typography>
                {item.barrel_size_kg && (
                  <Typography variant="caption" color="text.secondary">
                    {item.barrel_size_kg} kg/barrel
                  </Typography>
                )}
              </Box>
              <Box sx={{ p: 1.5, bgcolor: warningColor ? "transparent" : "#f5faf7" }}>
                <Typography variant="caption" color="text.secondary">
                  Printing Department
                </Typography>
                <Typography sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" }, fontWeight: 800, lineHeight: 1 }}>
                  {Number(item.department_kg || 0).toFixed(2)} kg
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {Number(item.department_barrels || 0).toFixed(2)} barrels
                </Typography>
                {item.barrel_size_kg && (
                  <Typography variant="caption" color="text.secondary">
                    {item.barrel_size_kg} kg/barrel
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}

function DyeStockHistoryDialog({
  item,
  onClose,
}: {
  item: any | null;
  onClose: () => void;
}) {
  const [history, setHistory] = useState<DyeStockItemHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!item) {
      setHistory(null);
      setError("");
      return;
    }

    let active = true;
    setLoading(true);
    setError("");
    dyeStockService.getItemHistory(Number(item.id))
      .then((result) => {
        if (active) setHistory(result);
      })
      .catch((requestError: any) => {
        if (active) {
          setError(
            requestError.response?.data?.error ||
              "Could not load this dye's stock history.",
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [item]);

  const chartData = useMemo(() => {
    const monthlyHistory = history?.monthlyHistory || [];
    if (!monthlyHistory.length) return [];

    const valuesByMonth = new Map(
      monthlyHistory.map((row) => [row.month, row]),
    );
    const [startYear, startMonth] = monthlyHistory[0].month.split("-").map(Number);
    const lastMonth = monthlyHistory[monthlyHistory.length - 1].month;
    const [lastYear, lastMonthNumber] = lastMonth.split("-").map(Number);
    const currentDate = new Date();
    const endYear = Math.max(lastYear, currentDate.getFullYear());
    const endMonth = endYear === currentDate.getFullYear()
      ? Math.max(lastMonthNumber, currentDate.getMonth() + 1)
      : lastMonthNumber;
    const rows = [];

    for (let cursor = new Date(startYear, startMonth - 1, 1);
      cursor <= new Date(endYear, endMonth - 1, 1);
      cursor.setMonth(cursor.getMonth() + 1)) {
      const month = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
      const source = valuesByMonth.get(month);
      rows.push({
        month,
        period: new Intl.DateTimeFormat("en", {
          month: "short",
          year: "2-digit",
        }).format(cursor),
        warehouseNet: Number(source?.warehouse_net_change_kg || 0),
        departmentNet: Number(source?.department_net_change_kg || 0),
      });
    }
    return rows;
  }, [history]);

  const itemData = history?.item;
  const monthlyRows = history?.monthlyHistory || [];
  const totals = monthlyRows.reduce(
    (result, row) => ({
      received: result.received + Number(row.warehouse_received_kg || 0),
      transferred: result.transferred + Number(row.transferred_to_department_kg || 0),
      issued: result.issued + Number(row.department_issued_kg || 0),
      returned: result.returned + Number(row.department_returned_kg || 0),
      transactions: result.transactions + Number(row.transaction_count || 0),
    }),
    { received: 0, transferred: 0, issued: 0, returned: 0, transactions: 0 },
  );
  const formatKg = (value: number | string | null | undefined) =>
    `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} kg`;
  const firstTransaction = history?.transactionRange.first_transaction_date
    ? new Date(history.transactionRange.first_transaction_date).toLocaleDateString()
    : null;
  const itemColor = /^#[0-9a-fA-F]{6}$/.test(itemData?.card_color || "")
    ? itemData!.card_color
    : "#00897B";
  const metrics = itemData
    ? [
        { label: "Total stock", value: formatKg(itemData.total_stock_kg), color: itemColor },
        { label: "Warehouse", value: formatKg(itemData.warehouse_kg), color: itemColor },
        { label: "Printing department", value: formatKg(itemData.department_kg), color: "#1565C0" },
        { label: "Monthly average usage", value: formatKg(itemData.monthly_average_usage_kg), color: "#6A1B9A" },
        {
          label: "Stock cover",
          value: Number(itemData.monthly_average_usage_kg) > 0 && itemData.months_remaining !== null
            ? `${Number(itemData.months_remaining).toFixed(1)} months`
            : "Usage not set",
          color: "#2E7D32",
        },
        { label: "Recorded transactions", value: String(totals.transactions), color: "#EF6C00" },
      ]
    : [];

  return (
    <Dialog open={Boolean(item)} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="div" fontWeight={700}>
            {itemData?.name || item?.name || "Dye stock history"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {itemData?.code ? `${itemData.code} · ` : ""}
            {itemData?.category === "chemical" ? "Chemical" : "Ink"} · Monthly stock movement
          </Typography>
        </Box>
        <IconButton aria-label="Close stock history" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
            <CircularProgress aria-label="Loading stock history" />
          </Box>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && itemData && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Monthly bars show net stock movement from recorded transactions, not month-end stock levels.
              {firstTransaction ? ` Recorded history starts ${firstTransaction}.` : " No transactions have been recorded yet."}
            </Alert>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {metrics.map((metric) => (
                <Grid item xs={6} sm={4} md={2} key={metric.label}>
                  <Card variant="outlined" sx={{ height: "100%", borderLeft: `4px solid ${metric.color}` }}>
                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">{metric.label}</Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: metric.color, overflowWrap: "anywhere" }}>
                        {metric.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Monthly net stock movement
              </Typography>
              {chartData.length ? (
                <Box sx={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" tick={{ fontSize: 11 }} minTickGap={18} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RechartsTooltip
                        formatter={(value, name) => [
                          `${Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} kg`,
                          String(name),
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="warehouseNet" name="Warehouse net change" fill={itemColor} />
                      <Bar dataKey="departmentNet" name="Department net change" fill="#1565C0" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ py: 5, textAlign: "center" }}>
                  No recorded transactions to chart for this dye.
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 } }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Monthly movement details
              </Typography>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Received</TableCell>
                      <TableCell align="right">Transferred to dept.</TableCell>
                      <TableCell align="right">Dept. issued</TableCell>
                      <TableCell align="right">Dept. returned</TableCell>
                      <TableCell align="right">Warehouse net</TableCell>
                      <TableCell align="right">Department net</TableCell>
                      <TableCell align="right">Transactions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...monthlyRows].reverse().map((row) => (
                      <TableRow key={row.month}>
                        <TableCell>{new Date(`${row.month}-01T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "long" })}</TableCell>
                        <TableCell align="right">{formatKg(row.warehouse_received_kg)}</TableCell>
                        <TableCell align="right">{formatKg(row.transferred_to_department_kg)}</TableCell>
                        <TableCell align="right">{formatKg(row.department_issued_kg)}</TableCell>
                        <TableCell align="right">{formatKg(row.department_returned_kg)}</TableCell>
                        <TableCell align="right">{formatKg(row.warehouse_net_change_kg)}</TableCell>
                        <TableCell align="right">{formatKg(row.department_net_change_kg)}</TableCell>
                        <TableCell align="right">{Number(row.transaction_count || 0)}</TableCell>
                      </TableRow>
                    ))}
                    {!monthlyRows.length && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">No monthly transaction data available.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary">
                Lifetime recorded movement: {formatKg(totals.received)} received · {formatKg(totals.transferred)} transferred to department · {formatKg(totals.issued)} issued · {formatKg(totals.returned)} returned.
              </Typography>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function TransactionLedger({
  transactions,
  reverseTransaction,
}: {
  transactions: any[];
  reverseTransaction: (id: number) => void;
}) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Item</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Barrels</TableCell>
            <TableCell align="right">kg</TableCell>
            <TableCell>From / To</TableCell>
            <TableCell>Reference</TableCell>
            <TableCell>User</TableCell>
            <TableCell align="right">Reverse</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((t) => (
            <TableRow key={t.id}>
              <TableCell>
                {String(t.transaction_date).replace("T", " ").slice(0, 16)}
              </TableCell>
              <TableCell>{t.item_name}</TableCell>
              <TableCell>
                {TRANSFER_TYPES[t.transaction_type] || t.transaction_type}
              </TableCell>
              <TableCell align="right">
                {Number(t.barrels).toFixed(3)}
              </TableCell>
              <TableCell align="right">{Number(t.kg).toFixed(3)}</TableCell>
              <TableCell>
                {t.from_location || "-"} → {t.to_location || "-"}
              </TableCell>
              <TableCell>{t.reference_number || "-"}</TableCell>
              <TableCell>{t.user_name || "-"}</TableCell>
              <TableCell align="right">
                <Tooltip title="Reverse transaction">
                  <IconButton
                    size="small"
                    onClick={() => reverseTransaction(t.id)}
                  >
                    <UndoIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
function MobileLedger({
  transactions,
  reverseTransaction,
}: {
  transactions: any[];
  reverseTransaction: (id: number) => void;
}) {
  return (
    <Box display="grid" gap={1}>
      {transactions.map((t) => (
        <Paper key={t.id} variant="outlined" sx={{ p: 1.5 }}>
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ fontWeight: 700 }}>{t.item_name}</Typography>
            <Tooltip title="Reverse transaction">
              <IconButton size="small" onClick={() => reverseTransaction(t.id)}>
                <UndoIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption">
            {TRANSFER_TYPES[t.transaction_type] || t.transaction_type}
          </Typography>
          <Typography variant="body2">
            {Number(t.barrels).toFixed(3)} barrels • {Number(t.kg).toFixed(3)}{" "}
            kg
          </Typography>
          <Typography variant="caption">
            {t.from_location || "-"} → {t.to_location || "-"}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

export default DyeStockManagement;